import React, { Component } from 'react';
import autobind from 'autobind-decorator';
import { interpret } from 'xstate';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import MacheteViewer from 'src/components/DesignPro/MacheteViewer';
import MacheteViewerPortal from 'src/components/DesignPro/MacheteViewerPortal';
import appDux from 'src/redux/app';
import uiDux from 'src/redux/ui';
import trackingDux from 'src/redux/tracking';
import ModeSelector from 'src/components/ModeSelector/ModeSelector';
import DesignPicker from 'src/components/DesignPicker/DesignPicker';
import SaveDesignButton from 'src/components/SaveDesignButton';
import Footer from 'src/components/Footer/Footer';
import ControlBar from 'src/components/ControlBar/ControlBar';
import ToolButtons from 'src/components/ToolButtons/ToolButtons';
import EditSplitButton from 'src/components/DesignSidebar/SplitTools/SplitButtons/EditSplitButton';
import breakpointUtils from 'src/utils/breakpointUtils';
import EditOriginalConfirmationModal from 'src/components/EditOriginalConfirmationModal';
import JobShareModal from 'src/components/JobShareModal/JobShareModal';
import Notification from 'src/components/Notification';
import SaveConfirmationModal from 'src/components/SaveConfirmationModal';
import Sidebar from 'src/components/Sidebar';
import { generateScreenshotMachine } from 'src/machines/screenshotMachine';
// NOTE - We need this polyfill to enable touch events on MS Edge
import 'src/utils/touchPolyfillUtil';
import { ViewerContainer } from './DesignProStyled';
import ScreenshotMode from '../ScreenshotMode';

const macheteRoot = <div id="machete-root" />;

export const mapStateToProps = (state) => ({
  jobId: state.designPro.app.jobDetails.id,
  metricData: trackingDux.selectors.getCommonMetricData(state),
  isInitialized: state.designPro.app.initialized,
  isInScreenshotMode: appDux.selectors.getIsScreenshotModeActive(state),
  isSidebarOpen: state.designPro.ui.isSidebarOpen,
  shouldRenderControlBar: uiDux.selectors.shouldRenderControlBar(state),
});

export const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      handleCloseScreenshotModeClick:
        uiDux.actions.handleCloseScreenshotModeClick,
    },
    dispatch,
  );

export class DesignPro extends Component {
  // eslint-disable-next-line
  static propTypes = {
    handleCloseScreenshotModeClick: PropTypes.func.isRequired,
    isInitialized: PropTypes.bool.isRequired,
    isInScreenshotMode: PropTypes.bool.isRequired,
    isSidebarOpen: PropTypes.bool.isRequired,
    jobId: PropTypes.number,
    shouldRenderControlBar: PropTypes.bool.isRequired,
    metricData: PropTypes.object.isRequired,
  };

  @autobind
  initializeServices() {
    this.screenshotMachineRef = interpret(
      generateScreenshotMachine(this.props.jobId, this.props.metricData),
    );

    this.screenshotMachineRef.start();
  }

  @autobind
  tearDownServices() {
    this.screenshotMachineRef.stop();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isInitialized !== this.props.isInitialized) {
      this.initializeServices();
    }
  }

  componentWillUnmount() {
    this.tearDownServices();
  }

  render() {
    const {
      handleCloseScreenshotModeClick,
      isInitialized,
      isInScreenshotMode,
      isSidebarOpen,
      shouldRenderControlBar,
    } = this.props;

    const isOnMobileDevice = breakpointUtils.isMobileDevice();

    return (
      <>
        {isInScreenshotMode ? (
          <ScreenshotMode
            screenshotMachineRef={this.screenshotMachineRef}
            macheteRoot={macheteRoot}
            handleCloseScreenshotModeClick={handleCloseScreenshotModeClick}
          />
        ) : (
          <>
            <ViewerContainer
              isSidebarOpen={isSidebarOpen}
              isInScreenshotMode={isInScreenshotMode}
            >
              {macheteRoot}
              {isInitialized && (
                <>
                  <Footer />
                  <ModeSelector />
                  {shouldRenderControlBar && <ControlBar />}
                  {!isOnMobileDevice && (
                    <>
                      <SaveDesignButton />
                      {isSidebarOpen && <ToolButtons />}
                      {isSidebarOpen && <EditSplitButton />}
                    </>
                  )}
                  <DesignPicker />
                </>
              )}
            </ViewerContainer>
            {isInitialized && (
              <>
                <Sidebar />
                <JobShareModal />
                <SaveConfirmationModal />
                <EditOriginalConfirmationModal />
                <Notification />
              </>
            )}
          </>
        )}
        <MacheteViewerPortal>
          <MacheteViewer />
        </MacheteViewerPortal>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DesignPro);
